/*  Shared  */
import {
    IdentityIntegrationStatusLevels,
    IdentityIntegrationStatusOrder,
    RISKS
} from 'application/Shared/Constants';
import { PROVIDER_TYPE_ORDER } from 'application/Shared/types/tenantProvider';

import { getLicenseDataStatus } from '../LicenseUsage/functions';
import { LicenseDataStatus } from '../LicenseUsage/types';

const transformConnectionDetails = (provider) => {
    if (!provider) {
        return IdentityIntegrationStatusLevels.NoIntegration;
    }
    if (provider?.connectionDetails?.isUpdateRequired) {
        return IdentityIntegrationStatusLevels.UpdateRequired;
    }
    if (provider?.enabled) {
        return IdentityIntegrationStatusLevels.Connected;
    }
    if (provider) {
        return IdentityIntegrationStatusLevels.Disconnected;
    }
    return null;
};

export const sortByIdentityIntegrationStatus = (rowA, rowB) => {
    const providersA = rowA.original.provider?.length || 0;
    const providersB = rowB.original.provider?.length || 0;

    // Sort by number of providers (descending - more connections first)
    if (providersA > providersB) return -1;
    if (providersA < providersB) return 1;

    // If same number of providers, sort by connection status across all providers
    const getBestStatus = (providers) => {
        if (!providers || providers.length === 0) return null;

        // Get all statuses and find the best one (lowest index in IdentityIntegrationStatusOrder)
        const statuses = providers.map((provider) =>
            transformConnectionDetails(provider)
        );
        return statuses.reduce((best, current) => {
            const bestIndex = IdentityIntegrationStatusOrder.indexOf(best);
            const currentIndex =
                IdentityIntegrationStatusOrder.indexOf(current);
            return currentIndex < bestIndex ? current : best;
        });
    };

    const statusA = getBestStatus(rowA.original.provider);
    const statusB = getBestStatus(rowB.original.provider);

    const statusComparison =
        IdentityIntegrationStatusOrder.indexOf(statusA) -
        IdentityIntegrationStatusOrder.indexOf(statusB);

    // If same connection status, sort by provider type
    if (statusComparison !== 0) {
        return statusComparison;
    }

    // Sort by provider type (Google, Microsoft, Okta)
    const getBestProviderType = (providers) => {
        if (!providers || providers.length === 0) return null;

        // Find the provider with the best status and return its type
        const bestStatus = getBestStatus(providers);
        const bestProvider = providers.find(
            (provider) => transformConnectionDetails(provider) === bestStatus
        );
        return bestProvider?.providerType;
    };

    const providerTypeA = getBestProviderType(rowA.original.provider);
    const providerTypeB = getBestProviderType(rowB.original.provider);

    return (
        PROVIDER_TYPE_ORDER.indexOf(providerTypeA) -
        PROVIDER_TYPE_ORDER.indexOf(providerTypeB)
    );
};

export const sortBySmartSortFn = (rowA, rowB) => {
    if (rowA.original.smartSort > rowB.original.smartSort) return 1;
    if (rowA.original.smartSort < rowB.original.smartSort) return -1;
    return 0;
};

export const sortByLicenseStatus = (rowA, rowB) => {
    const statusOrder = {
        [LicenseDataStatus.available]: 1,
        [LicenseDataStatus.syncFailed]: 2,
        [LicenseDataStatus.notConnected]: 3,
        [LicenseDataStatus.unavailable]: 4
    };

    const statusA = getLicenseDataStatus(rowA.original);
    const statusB = getLicenseDataStatus(rowB.original);

    return statusOrder[statusA] - statusOrder[statusB];
};

// TODO: nulls at the end: can't be done without sorting direction: https://github.com/TanStack/table/discussions/4920
export const sortByRiskFn = (rowA, rowB) => {
    const riskA = rowA.original.risk;
    const riskB = rowB.original.risk;

    return RISKS.indexOf(riskA) - RISKS.indexOf(riskB);
};

export const sortByTimestamp = (rowA, rowB, columnId) => {
    const dateA = new Date(rowA.original[columnId]);
    const dateB = new Date(rowB.original[columnId]);

    if (dateA > dateB) return 1;
    if (dateA < dateB) return -1;
    return 0;
};

// Sorting for user count column on client apps table
export const sortByUserCount = (rowA, rowB) => {
    const userCountA = rowA.original.userCount || 0;
    const userCountB = rowB.original.userCount || 0;

    if (userCountA > userCountB) return 1;
    if (userCountA < userCountB) return -1;
    return 0;
};

export const sortByRanking = (rowA, rowB, columnId) => {
    if (rowA.original[columnId] === null) return 1;
    if (rowB.original[columnId] === null) return -1;
    if (rowA.original[columnId] > rowB.original[columnId]) return 1;
    if (rowA.original[columnId] < rowB.original[columnId]) return -1;
    return 0;
};

export const sortByWorkatoProvider = (rows) => {
    if (!Array.isArray(rows)) return rows;

    const sortedRows = rows.sort((a, b) => {
        const workatoProviderA = a.application?.workatoProvider;
        const workatoProviderB = b.application?.workatoProvider;

        // If both have workatoProviders, sort by name
        if (
            workatoProviderA &&
            workatoProviderB &&
            b.application?.name &&
            a.application?.name
        ) {
            return a.application?.name.localeCompare(b.application?.name);
        }

        // If only one has a workatoProvider, sort the one with a workatoProvider first
        if (workatoProviderA && !workatoProviderB) {
            return -1;
        }
        if (!workatoProviderA && workatoProviderB) {
            return 1;
        }

        // If neither has a provider, sort by name
        if (a.application?.name && b.application?.name) {
            return a.application?.name.localeCompare(b.application?.name);
        }
        return 0;
    });

    return sortedRows;
};
